import * as Schema from "effect/Schema";
import * as Rpc from "effect/unstable/rpc/Rpc";
import * as RpcGroup from "effect/unstable/rpc/RpcGroup";

import { ExternalLauncherError, LaunchEditorInput } from "./editor.ts";
import {
  AuthAccessStreamError,
  AuthAccessStreamEvent,
  EnvironmentAuthorizationError,
} from "./auth.ts";
import {
  FilesystemBrowseInput,
  FilesystemBrowseResult,
  FilesystemBrowseError,
} from "./filesystem.ts";
import { AssetAccessError, AssetCreateUrlInput, AssetCreateUrlResult } from "./assets.ts";
import {
  GitActionProgressEvent,
  VcsSwitchRefInput,
  VcsSwitchRefResult,
  GitCommandError,
  VcsCreateRefInput,
  VcsCreateRefResult,
  VcsCreateWorktreeInput,
  VcsCreateWorktreeResult,
  VcsInitInput,
  VcsListRefsInput,
  VcsListRefsResult,
  GitManagerServiceError,
  GitPreparePullRequestThreadInput,
  GitPreparePullRequestThreadResult,
  VcsPullInput,
  GitPullRequestRefInput,
  VcsPullResult,
  VcsRemoveWorktreeInput,
  GitResolvePullRequestResult,
  GitRunStackedActionInput,
  VcsStatusInput,
  VcsStatusResult,
  VcsStatusStreamEvent,
} from "./git.ts";
import {
  ReviewDiffPreviewError,
  ReviewDiffPreviewInput,
  ReviewDiffPreviewResult,
} from "./review.ts";
import { KeybindingsConfigError } from "./keybindings.ts";
import {
  ClientOrchestrationCommand,
  ORCHESTRATION_WS_METHODS,
  OrchestrationDispatchCommandError,
  OrchestrationGetFullThreadDiffError,
  OrchestrationGetFullThreadDiffInput,
  OrchestrationGetSnapshotError,
  OrchestrationGetTurnDiffError,
  OrchestrationGetTurnDiffInput,
  OrchestrationReplayEventsError,
  OrchestrationReplayEventsInput,
  OrchestrationRpcSchemas,
} from "./orchestration.ts";
import { ProviderInstanceId } from "./providerInstance.ts";
import {
  RelayClientInstallFailedError,
  RelayClientInstallProgressEventSchema,
  RelayClientStatusSchema,
} from "./relayClient.ts";
import {
  ProjectListEntriesError,
  ProjectListEntriesInput,
  ProjectListEntriesResult,
  ProjectReadFileError,
  ProjectReadFileInput,
  ProjectReadFileResult,
  ProjectSearchEntriesError,
  ProjectSearchEntriesInput,
  ProjectSearchEntriesResult,
  ProjectWriteFileError,
  ProjectWriteFileInput,
  ProjectWriteFileResult,
} from "./project.ts";
import {
  TerminalAttachInput,
  TerminalAttachStreamEvent,
  TerminalClearInput,
  TerminalCloseInput,
  TerminalError,
  TerminalEvent,
  TerminalMetadataStreamEvent,
  TerminalOpenInput,
  TerminalResizeInput,
  TerminalRestartInput,
  TerminalSessionSnapshot,
  TerminalWriteInput,
} from "./terminal.ts";
import {
  DiscoveredLocalServerList,
  PreviewCloseInput,
  PreviewError,
  PreviewEvent,
  PreviewListInput,
  PreviewListResult,
  PreviewNavigateInput,
  PreviewOpenInput,
  PreviewRefreshInput,
  PreviewReportStatusInput,
  PreviewResizeInput,
  PreviewSessionSnapshot,
} from "./preview.ts";
import {
  PreviewAutomationError,
  PreviewAutomationHost,
  PreviewAutomationHostFocus,
  PreviewAutomationResponse,
  PreviewAutomationStreamEvent,
} from "./previewAutomation.ts";
import {
  ServerConfigStreamEvent,
  ServerConfig,
  ServerProviderUpdateError,
  ServerProviderUpdateInput,
  ServerLifecycleStreamEvent,
  ServerRemoveKeybindingInput,
  ServerRemoveKeybindingResult,
  ServerProviderUpdatedPayload,
  ServerSelfUpdateError,
  ServerSelfUpdateInput,
  ServerSelfUpdateResult,
  ServerTraceDiagnosticsResult,
  ServerProcessDiagnosticsResult,
  ServerProcessResourceHistoryInput,
  ServerProcessResourceHistoryResult,
  ServerSignalProcessInput,
  ServerSignalProcessResult,
  ServerUpsertKeybindingInput,
  ServerUpsertKeybindingResult,
} from "./server.ts";
import {
  AgentWorkflowInputError,
  ServerSettings,
  ServerSettingsError,
  ServerSettingsPatch,
} from "./settings.ts";
import {
  McpGatewayActivatePluginInput,
  McpGatewayDeactivatePluginInput,
  McpGatewayError,
  McpGatewayPluginListing,
  McpGatewayPluginState,
  McpGatewaySetConnectionInput,
  McpGatewayStartOAuthInput,
  McpGatewayStartOAuthResult,
  McpGatewayStatus,
} from "./mcpGateway.ts";
import {
  SourceControlCloneRepositoryInput,
  SourceControlCloneRepositoryResult,
  SourceControlDiscoveryResult,
  SourceControlPublishRepositoryInput,
  SourceControlPublishRepositoryResult,
  SourceControlRepositoryError,
  SourceControlRepositoryInfo,
  SourceControlRepositoryLookupInput,
} from "./sourceControl.ts";
import { VcsError } from "./vcs.ts";
import {
  VoiceConversationAppendAudioInput,
  VoiceConversationAppendAudioResult,
  VoiceConversationEndInputInput,
  VoiceConversationEndInputResult,
  VoiceConversationEvent,
  VoiceConversationEventsInput,
  VoiceConversationStartInput,
  VoiceConversationStartResult,
  VoiceConversationStopInput,
  VoiceConversationStopResult,
  VoiceServiceError,
  VoiceSynthesizeSpeechInput,
  VoiceTranscribeClipInput,
  VoiceTranscriptResult,
  VoiceAudioChunk,
  VOICE_WS_METHODS,
} from "./voice.ts";

export const WS_METHODS = {
  // Project registry methods
  projectsList: "projects.list",
  projectsAdd: "projects.add",
  projectsRemove: "projects.remove",
  projectsListEntries: "projects.listEntries",
  projectsReadFile: "projects.readFile",
  projectsSearchEntries: "projects.searchEntries",
  projectsWriteFile: "projects.writeFile",

  // Shell methods
  shellOpenInEditor: "shell.openInEditor",

  // Filesystem methods
  filesystemBrowse: "filesystem.browse",
  assetsCreateUrl: "assets.createUrl",

  // VCS methods
  vcsPull: "vcs.pull",
  vcsRefreshStatus: "vcs.refreshStatus",
  vcsListRefs: "vcs.listRefs",
  vcsCreateWorktree: "vcs.createWorktree",
  vcsRemoveWorktree: "vcs.removeWorktree",
  vcsCreateRef: "vcs.createRef",
  vcsSwitchRef: "vcs.switchRef",
  vcsInit: "vcs.init",

  // Git workflow methods
  gitRunStackedAction: "git.runStackedAction",
  gitResolvePullRequest: "git.resolvePullRequest",
  gitPreparePullRequestThread: "git.preparePullRequestThread",

  // Review methods
  reviewGetDiffPreview: "review.getDiffPreview",

  // Terminal methods
  terminalOpen: "terminal.open",
  terminalAttach: "terminal.attach",
  terminalWrite: "terminal.write",
  terminalResize: "terminal.resize",
  terminalClear: "terminal.clear",
  terminalRestart: "terminal.restart",
  terminalClose: "terminal.close",

  // Preview methods
  previewOpen: "preview.open",
  previewNavigate: "preview.navigate",
  previewResize: "preview.resize",
  previewRefresh: "preview.refresh",
  previewClose: "preview.close",
  previewList: "preview.list",
  previewReportStatus: "preview.reportStatus",
  previewAutomationConnect: "previewAutomation.connect",
  previewAutomationRespond: "previewAutomation.respond",
  previewAutomationFocusHost: "previewAutomation.focusHost",

  // Server meta
  serverProbe: "server.probe",
  serverGetConfig: "server.getConfig",
  serverRefreshProviders: "server.refreshProviders",
  serverUpdateProvider: "server.updateProvider",
  serverUpdateServer: "server.updateServer",
  serverUpsertKeybinding: "server.upsertKeybinding",
  serverRemoveKeybinding: "server.removeKeybinding",
  serverGetSettings: "server.getSettings",
  serverUpdateSettings: "server.updateSettings",

  // MCP gateway methods
  mcpGatewayGetStatus: "mcpGateway.getStatus",
  mcpGatewayTestConnection: "mcpGateway.testConnection",
  mcpGatewaySetConnection: "mcpGateway.setConnection",
  mcpGatewayListPlugins: "mcpGateway.listPlugins",
  mcpGatewayActivatePlugin: "mcpGateway.activatePlugin",
  mcpGatewayDeactivatePlugin: "mcpGateway.deactivatePlugin",
  mcpGatewayStartOAuth: "mcpGateway.startOAuth",

  // Werkflow ERP instance methods
  werkflowInstancesTestConnection: "werkflowInstances.testConnection",
  agentWorkflowsGetStore: "agentWorkflows.getStore",
  agentWorkflowsCreate: "agentWorkflows.create",
  agentWorkflowsUpdateSchedule: "agentWorkflows.updateSchedule",
  agentWorkflowsApplyChatCommand: "agentWorkflows.applyChatCommand",
  agentWorkflowsRunOnce: "agentWorkflows.runOnce",
  agentWorkflowsResolveApproval: "agentWorkflows.resolveApproval",
  agentWorkflowsCancelRun: "agentWorkflows.cancelRun",
  agentWorkflowsValidate: "agentWorkflows.validate",
  agentWorkflowsApproveVersion: "agentWorkflows.approveVersion",
  agentWorkflowsDeployVersion: "agentWorkflows.deployVersion",
  agentWorkflowsGetDeployment: "agentWorkflows.getDeployment",
  agentWorkflowsRestartRun: "agentWorkflows.restartRun",
  agentWorkflowsReconcileRun: "agentWorkflows.reconcileRun",
  convexWorkflowSecretsSet: "agentWorkflows.convexSecretsSet",
  convexWorkflowSecretsStatus: "agentWorkflows.convexSecretsStatus",
  subscribeAgentWorkflowRuns: "subscribeAgentWorkflowRuns",
  serverDiscoverSourceControl: "server.discoverSourceControl",
  serverGetTraceDiagnostics: "server.getTraceDiagnostics",
  serverGetProcessDiagnostics: "server.getProcessDiagnostics",
  serverGetProcessResourceHistory: "server.getProcessResourceHistory",
  serverSignalProcess: "server.signalProcess",

  // Cloud environment methods
  cloudGetRelayClientStatus: "cloud.getRelayClientStatus",
  cloudInstallRelayClient: "cloud.installRelayClient",

  // Source control methods
  sourceControlLookupRepository: "sourceControl.lookupRepository",
  sourceControlCloneRepository: "sourceControl.cloneRepository",
  sourceControlPublishRepository: "sourceControl.publishRepository",

  // Streaming subscriptions
  subscribeVcsStatus: "subscribeVcsStatus",
  subscribeTerminalEvents: "subscribeTerminalEvents",
  subscribeTerminalMetadata: "subscribeTerminalMetadata",
  subscribePreviewEvents: "subscribePreviewEvents",
  subscribeDiscoveredLocalServers: "subscribeDiscoveredLocalServers",
  subscribeServerConfig: "subscribeServerConfig",
  subscribeServerLifecycle: "subscribeServerLifecycle",
  subscribeAuthAccess: "subscribeAuthAccess",
} as const;

export const WsServerUpsertKeybindingRpc = Rpc.make(WS_METHODS.serverUpsertKeybinding, {
  payload: ServerUpsertKeybindingInput,
  success: ServerUpsertKeybindingResult,
  error: Schema.Union([KeybindingsConfigError, EnvironmentAuthorizationError]),
});

export const WsServerRemoveKeybindingRpc = Rpc.make(WS_METHODS.serverRemoveKeybinding, {
  payload: ServerRemoveKeybindingInput,
  success: ServerRemoveKeybindingResult,
  error: Schema.Union([KeybindingsConfigError, EnvironmentAuthorizationError]),
});

export const WsServerProbeRpc = Rpc.make(WS_METHODS.serverProbe, {
  payload: Schema.Struct({}),
  success: Schema.Struct({}),
  error: EnvironmentAuthorizationError,
});

export const WsServerGetConfigRpc = Rpc.make(WS_METHODS.serverGetConfig, {
  payload: Schema.Struct({}),
  success: ServerConfig,
  error: Schema.Union([KeybindingsConfigError, ServerSettingsError, EnvironmentAuthorizationError]),
});

export const WsServerRefreshProvidersRpc = Rpc.make(WS_METHODS.serverRefreshProviders, {
  payload: Schema.Struct({
    /**
     * When supplied, only refresh this specific provider instance. When
     * omitted, refresh all configured instances — the legacy `refresh()`
     * behaviour retained for transports that still dispatch untargeted
     * refreshes.
     */
    instanceId: Schema.optional(ProviderInstanceId),
  }),
  success: ServerProviderUpdatedPayload,
  error: EnvironmentAuthorizationError,
});

export const WsServerUpdateProviderRpc = Rpc.make(WS_METHODS.serverUpdateProvider, {
  payload: ServerProviderUpdateInput,
  success: ServerProviderUpdatedPayload,
  error: Schema.Union([ServerProviderUpdateError, EnvironmentAuthorizationError]),
});

export const WsServerUpdateServerRpc = Rpc.make(WS_METHODS.serverUpdateServer, {
  payload: ServerSelfUpdateInput,
  success: ServerSelfUpdateResult,
  error: Schema.Union([ServerSelfUpdateError, EnvironmentAuthorizationError]),
});

export const WsServerGetSettingsRpc = Rpc.make(WS_METHODS.serverGetSettings, {
  payload: Schema.Struct({}),
  success: ServerSettings,
  error: Schema.Union([ServerSettingsError, EnvironmentAuthorizationError]),
});

export const WsServerUpdateSettingsRpc = Rpc.make(WS_METHODS.serverUpdateSettings, {
  payload: Schema.Struct({ patch: ServerSettingsPatch }),
  success: ServerSettings,
  error: Schema.Union([ServerSettingsError, EnvironmentAuthorizationError]),
});

export const WsMcpGatewayGetStatusRpc = Rpc.make(WS_METHODS.mcpGatewayGetStatus, {
  payload: Schema.Struct({}),
  success: McpGatewayStatus,
  error: Schema.Union([McpGatewayError, EnvironmentAuthorizationError]),
});

export const WsMcpGatewayTestConnectionRpc = Rpc.make(WS_METHODS.mcpGatewayTestConnection, {
  payload: Schema.Struct({}),
  success: McpGatewayStatus,
  error: Schema.Union([McpGatewayError, EnvironmentAuthorizationError]),
});

export const WsMcpGatewaySetConnectionRpc = Rpc.make(WS_METHODS.mcpGatewaySetConnection, {
  payload: McpGatewaySetConnectionInput,
  success: McpGatewayStatus,
  error: Schema.Union([McpGatewayError, ServerSettingsError, EnvironmentAuthorizationError]),
});

export const WsMcpGatewayListPluginsRpc = Rpc.make(WS_METHODS.mcpGatewayListPlugins, {
  payload: Schema.Struct({}),
  success: Schema.Array(McpGatewayPluginListing),
  error: Schema.Union([McpGatewayError, EnvironmentAuthorizationError]),
});

export const WsMcpGatewayActivatePluginRpc = Rpc.make(WS_METHODS.mcpGatewayActivatePlugin, {
  payload: McpGatewayActivatePluginInput,
  success: McpGatewayPluginState,
  error: Schema.Union([McpGatewayError, EnvironmentAuthorizationError]),
});

export const WsMcpGatewayDeactivatePluginRpc = Rpc.make(WS_METHODS.mcpGatewayDeactivatePlugin, {
  payload: McpGatewayDeactivatePluginInput,
  success: McpGatewayPluginState,
  error: Schema.Union([McpGatewayError, EnvironmentAuthorizationError]),
});

export const WsMcpGatewayStartOAuthRpc = Rpc.make(WS_METHODS.mcpGatewayStartOAuth, {
  payload: McpGatewayStartOAuthInput,
  success: McpGatewayStartOAuthResult,
  error: Schema.Union([McpGatewayError, EnvironmentAuthorizationError]),
});

/**
 * Result of probing an ERP instance via `GET /api/v1/me`. Never fails the RPC
 * for connection/auth problems — those surface as `{ ok: false, error }` so the
 * settings UI can render a friendly message. The error channel is reserved for
 * environment-authorization failures.
 */
export const WerkflowTestConnectionResult = Schema.Struct({
  ok: Schema.Boolean,
  workspaceName: Schema.optional(Schema.String),
  keyLabel: Schema.optional(Schema.String),
  scopeCount: Schema.optional(Schema.Number),
  writeReady: Schema.optional(Schema.Boolean),
  missingWriteScopes: Schema.optional(Schema.Array(Schema.String)),
  error: Schema.optional(Schema.String),
});
export type WerkflowTestConnectionResult = typeof WerkflowTestConnectionResult.Type;

export const WsWerkflowInstancesTestConnectionRpc = Rpc.make(
  WS_METHODS.werkflowInstancesTestConnection,
  {
    payload: Schema.Struct({ instanceId: Schema.String }),
    success: WerkflowTestConnectionResult,
    error: EnvironmentAuthorizationError,
  },
);

export const WsAgentWorkflowsGetStoreRpc = Rpc.make(WS_METHODS.agentWorkflowsGetStore, {
  payload: Schema.Struct({ tenantId: Schema.String }),
  success: Schema.Unknown,
  error: Schema.Union([ServerSettingsError, EnvironmentAuthorizationError]),
});

export const WsAgentWorkflowsCreateRpc = Rpc.make(WS_METHODS.agentWorkflowsCreate, {
  payload: Schema.Struct({
    tenantId: Schema.String,
    name: Schema.String,
    instruction: Schema.String,
    // Schedule preset selected in the sidebar. Structurally an
    // `AgentWorkflowSchedule`; kept `Unknown` here to match the sibling
    // agentWorkflows RPCs and avoid duplicating the schema in contracts.
    schedule: Schema.Unknown,
  }),
  success: Schema.Unknown,
  error: Schema.Union([
    AgentWorkflowInputError,
    ServerSettingsError,
    EnvironmentAuthorizationError,
  ]),
});

export const WsAgentWorkflowsUpdateScheduleRpc = Rpc.make(WS_METHODS.agentWorkflowsUpdateSchedule, {
  payload: Schema.Struct({
    tenantId: Schema.String,
    workflowId: Schema.String,
    schedule: Schema.Unknown,
  }),
  success: Schema.Unknown,
  error: Schema.Union([ServerSettingsError, EnvironmentAuthorizationError]),
});

export const WsAgentWorkflowsApplyChatCommandRpc = Rpc.make(
  WS_METHODS.agentWorkflowsApplyChatCommand,
  {
    payload: Schema.Struct({
      tenantId: Schema.String,
      prompt: Schema.String,
    }),
    success: Schema.Unknown,
    error: Schema.Union([
      AgentWorkflowInputError,
      ServerSettingsError,
      EnvironmentAuthorizationError,
    ]),
  },
);

export const WsAgentWorkflowsRunOnceRpc = Rpc.make(WS_METHODS.agentWorkflowsRunOnce, {
  payload: Schema.Struct({
    tenantId: Schema.String,
    workflowId: Schema.String,
  }),
  success: Schema.Unknown,
  error: Schema.Union([ServerSettingsError, EnvironmentAuthorizationError]),
});

export const WsAgentWorkflowsResolveApprovalRpc = Rpc.make(
  WS_METHODS.agentWorkflowsResolveApproval,
  {
    payload: Schema.Struct({
      tenantId: Schema.String,
      runId: Schema.String,
      decision: Schema.Literals(["approve", "decline"]),
    }),
    success: Schema.Unknown,
    error: Schema.Union([ServerSettingsError, EnvironmentAuthorizationError]),
  },
);

export const WsAgentWorkflowsCancelRunRpc = Rpc.make(WS_METHODS.agentWorkflowsCancelRun, {
  payload: Schema.Struct({
    tenantId: Schema.String,
    runId: Schema.String,
  }),
  success: Schema.Unknown,
  error: Schema.Union([ServerSettingsError, EnvironmentAuthorizationError]),
});

const ConvexWorkflowVersionPayload = Schema.Struct({
  tenantId: Schema.String,
  workflowId: Schema.String,
  sourceVersion: Schema.Number,
});

export const WsAgentWorkflowsValidateRpc = Rpc.make(WS_METHODS.agentWorkflowsValidate, {
  payload: Schema.Struct({ tenantId: Schema.String, workflowId: Schema.String }),
  success: Schema.Unknown,
  error: Schema.Union([ServerSettingsError, EnvironmentAuthorizationError]),
});
export const WsAgentWorkflowsApproveVersionRpc = Rpc.make(WS_METHODS.agentWorkflowsApproveVersion, {
  payload: ConvexWorkflowVersionPayload,
  success: Schema.Unknown,
  error: Schema.Union([ServerSettingsError, EnvironmentAuthorizationError]),
});
export const WsAgentWorkflowsDeployVersionRpc = Rpc.make(WS_METHODS.agentWorkflowsDeployVersion, {
  payload: ConvexWorkflowVersionPayload,
  success: Schema.Unknown,
  error: Schema.Union([ServerSettingsError, EnvironmentAuthorizationError]),
});
export const WsAgentWorkflowsGetDeploymentRpc = Rpc.make(WS_METHODS.agentWorkflowsGetDeployment, {
  payload: ConvexWorkflowVersionPayload,
  success: Schema.Unknown,
  error: Schema.Union([ServerSettingsError, EnvironmentAuthorizationError]),
});
export const WsAgentWorkflowsRestartRunRpc = Rpc.make(WS_METHODS.agentWorkflowsRestartRun, {
  payload: Schema.Struct({
    tenantId: Schema.String,
    runId: Schema.String,
    from: Schema.optional(Schema.String),
  }),
  success: Schema.Unknown,
  error: Schema.Union([ServerSettingsError, EnvironmentAuthorizationError]),
});
export const ConvexWorkflowSecretKind = Schema.Literals([
  "deploy-key",
  "runtime-token",
  "callback-secret",
]);
export const WsConvexWorkflowSecretsSetRpc = Rpc.make(WS_METHODS.convexWorkflowSecretsSet, {
  payload: Schema.Struct({
    deploymentRef: Schema.String,
    kind: ConvexWorkflowSecretKind,
    value: Schema.String,
  }),
  success: Schema.Unknown,
  error: Schema.Union([ServerSettingsError, EnvironmentAuthorizationError]),
});
export const WsConvexWorkflowSecretsStatusRpc = Rpc.make(WS_METHODS.convexWorkflowSecretsStatus, {
  payload: Schema.Struct({ deploymentRef: Schema.String }),
  success: Schema.Struct({
    deployKey: Schema.Boolean,
    runtimeToken: Schema.Boolean,
    callbackSecret: Schema.Boolean,
  }),
  error: Schema.Union([ServerSettingsError, EnvironmentAuthorizationError]),
});
export const WsAgentWorkflowsReconcileRunRpc = Rpc.make(WS_METHODS.agentWorkflowsReconcileRun, {
  payload: Schema.Struct({ tenantId: Schema.String, runId: Schema.String }),
  success: Schema.Unknown,
  error: Schema.Union([ServerSettingsError, EnvironmentAuthorizationError]),
});

export const WsSubscribeAgentWorkflowRunsRpc = Rpc.make(WS_METHODS.subscribeAgentWorkflowRuns, {
  payload: Schema.Struct({ tenantId: Schema.String }),
  success: Schema.Unknown,
  error: Schema.Union([ServerSettingsError, EnvironmentAuthorizationError]),
  stream: true,
});

export const WsServerDiscoverSourceControlRpc = Rpc.make(WS_METHODS.serverDiscoverSourceControl, {
  payload: Schema.Struct({}),
  success: SourceControlDiscoveryResult,
  error: EnvironmentAuthorizationError,
});

export const WsServerGetTraceDiagnosticsRpc = Rpc.make(WS_METHODS.serverGetTraceDiagnostics, {
  payload: Schema.Struct({}),
  success: ServerTraceDiagnosticsResult,
  error: EnvironmentAuthorizationError,
});

export const WsServerGetProcessDiagnosticsRpc = Rpc.make(WS_METHODS.serverGetProcessDiagnostics, {
  payload: Schema.Struct({}),
  success: ServerProcessDiagnosticsResult,
  error: EnvironmentAuthorizationError,
});

export const WsServerGetProcessResourceHistoryRpc = Rpc.make(
  WS_METHODS.serverGetProcessResourceHistory,
  {
    payload: ServerProcessResourceHistoryInput,
    success: ServerProcessResourceHistoryResult,
    error: EnvironmentAuthorizationError,
  },
);

export const WsServerSignalProcessRpc = Rpc.make(WS_METHODS.serverSignalProcess, {
  payload: ServerSignalProcessInput,
  success: ServerSignalProcessResult,
  error: EnvironmentAuthorizationError,
});

export const WsCloudGetRelayClientStatusRpc = Rpc.make(WS_METHODS.cloudGetRelayClientStatus, {
  payload: Schema.Struct({}),
  success: RelayClientStatusSchema,
  error: EnvironmentAuthorizationError,
});

export const WsCloudInstallRelayClientRpc = Rpc.make(WS_METHODS.cloudInstallRelayClient, {
  payload: Schema.Struct({}),
  success: RelayClientInstallProgressEventSchema,
  error: Schema.Union([RelayClientInstallFailedError, EnvironmentAuthorizationError]),
  stream: true,
});

export const WsSourceControlLookupRepositoryRpc = Rpc.make(
  WS_METHODS.sourceControlLookupRepository,
  {
    payload: SourceControlRepositoryLookupInput,
    success: SourceControlRepositoryInfo,
    error: Schema.Union([SourceControlRepositoryError, EnvironmentAuthorizationError]),
  },
);

export const WsSourceControlCloneRepositoryRpc = Rpc.make(WS_METHODS.sourceControlCloneRepository, {
  payload: SourceControlCloneRepositoryInput,
  success: SourceControlCloneRepositoryResult,
  error: Schema.Union([SourceControlRepositoryError, EnvironmentAuthorizationError]),
});

export const WsSourceControlPublishRepositoryRpc = Rpc.make(
  WS_METHODS.sourceControlPublishRepository,
  {
    payload: SourceControlPublishRepositoryInput,
    success: SourceControlPublishRepositoryResult,
    error: Schema.Union([SourceControlRepositoryError, EnvironmentAuthorizationError]),
  },
);

export const WsProjectsSearchEntriesRpc = Rpc.make(WS_METHODS.projectsSearchEntries, {
  payload: ProjectSearchEntriesInput,
  success: ProjectSearchEntriesResult,
  error: Schema.Union([ProjectSearchEntriesError, EnvironmentAuthorizationError]),
});

export const WsProjectsListEntriesRpc = Rpc.make(WS_METHODS.projectsListEntries, {
  payload: ProjectListEntriesInput,
  success: ProjectListEntriesResult,
  error: Schema.Union([ProjectListEntriesError, EnvironmentAuthorizationError]),
});

export const WsProjectsReadFileRpc = Rpc.make(WS_METHODS.projectsReadFile, {
  payload: ProjectReadFileInput,
  success: ProjectReadFileResult,
  error: Schema.Union([ProjectReadFileError, EnvironmentAuthorizationError]),
});

export const WsProjectsWriteFileRpc = Rpc.make(WS_METHODS.projectsWriteFile, {
  payload: ProjectWriteFileInput,
  success: ProjectWriteFileResult,
  error: Schema.Union([ProjectWriteFileError, EnvironmentAuthorizationError]),
});

export const WsShellOpenInEditorRpc = Rpc.make(WS_METHODS.shellOpenInEditor, {
  payload: LaunchEditorInput,
  error: Schema.Union([ExternalLauncherError, EnvironmentAuthorizationError]),
});

export const WsFilesystemBrowseRpc = Rpc.make(WS_METHODS.filesystemBrowse, {
  payload: FilesystemBrowseInput,
  success: FilesystemBrowseResult,
  error: Schema.Union([FilesystemBrowseError, EnvironmentAuthorizationError]),
});

export const WsAssetsCreateUrlRpc = Rpc.make(WS_METHODS.assetsCreateUrl, {
  payload: AssetCreateUrlInput,
  success: AssetCreateUrlResult,
  error: Schema.Union([AssetAccessError, EnvironmentAuthorizationError]),
});

export const WsSubscribeVcsStatusRpc = Rpc.make(WS_METHODS.subscribeVcsStatus, {
  payload: VcsStatusInput,
  success: VcsStatusStreamEvent,
  error: Schema.Union([GitManagerServiceError, EnvironmentAuthorizationError]),
  stream: true,
});

export const WsVcsPullRpc = Rpc.make(WS_METHODS.vcsPull, {
  payload: VcsPullInput,
  success: VcsPullResult,
  error: Schema.Union([GitCommandError, EnvironmentAuthorizationError]),
});

export const WsVcsRefreshStatusRpc = Rpc.make(WS_METHODS.vcsRefreshStatus, {
  payload: VcsStatusInput,
  success: VcsStatusResult,
  error: Schema.Union([GitManagerServiceError, EnvironmentAuthorizationError]),
});

export const WsGitRunStackedActionRpc = Rpc.make(WS_METHODS.gitRunStackedAction, {
  payload: GitRunStackedActionInput,
  success: GitActionProgressEvent,
  error: Schema.Union([GitManagerServiceError, EnvironmentAuthorizationError]),
  stream: true,
});

export const WsGitResolvePullRequestRpc = Rpc.make(WS_METHODS.gitResolvePullRequest, {
  payload: GitPullRequestRefInput,
  success: GitResolvePullRequestResult,
  error: Schema.Union([GitManagerServiceError, EnvironmentAuthorizationError]),
});

export const WsGitPreparePullRequestThreadRpc = Rpc.make(WS_METHODS.gitPreparePullRequestThread, {
  payload: GitPreparePullRequestThreadInput,
  success: GitPreparePullRequestThreadResult,
  error: Schema.Union([GitManagerServiceError, EnvironmentAuthorizationError]),
});

export const WsVcsListRefsRpc = Rpc.make(WS_METHODS.vcsListRefs, {
  payload: VcsListRefsInput,
  success: VcsListRefsResult,
  error: Schema.Union([GitCommandError, EnvironmentAuthorizationError]),
});

export const WsVcsCreateWorktreeRpc = Rpc.make(WS_METHODS.vcsCreateWorktree, {
  payload: VcsCreateWorktreeInput,
  success: VcsCreateWorktreeResult,
  error: Schema.Union([GitCommandError, EnvironmentAuthorizationError]),
});

export const WsVcsRemoveWorktreeRpc = Rpc.make(WS_METHODS.vcsRemoveWorktree, {
  payload: VcsRemoveWorktreeInput,
  error: Schema.Union([GitCommandError, EnvironmentAuthorizationError]),
});

export const WsVcsCreateRefRpc = Rpc.make(WS_METHODS.vcsCreateRef, {
  payload: VcsCreateRefInput,
  success: VcsCreateRefResult,
  error: Schema.Union([GitCommandError, EnvironmentAuthorizationError]),
});

export const WsVcsSwitchRefRpc = Rpc.make(WS_METHODS.vcsSwitchRef, {
  payload: VcsSwitchRefInput,
  success: VcsSwitchRefResult,
  error: Schema.Union([GitCommandError, EnvironmentAuthorizationError]),
});

export const WsVcsInitRpc = Rpc.make(WS_METHODS.vcsInit, {
  payload: VcsInitInput,
  error: Schema.Union([VcsError, EnvironmentAuthorizationError]),
});

/**
 * Ephemeral live diff preview for compact/mobile surfaces.
 * Not the persisted T3 Review model. Future review sessions should use
 * review.open* + review.getSnapshot.
 */
export const WsReviewGetDiffPreviewRpc = Rpc.make(WS_METHODS.reviewGetDiffPreview, {
  payload: ReviewDiffPreviewInput,
  success: ReviewDiffPreviewResult,
  error: Schema.Union([ReviewDiffPreviewError, EnvironmentAuthorizationError]),
});

export const WsVoiceTranscribeClipRpc = Rpc.make(VOICE_WS_METHODS.transcribeClip, {
  payload: VoiceTranscribeClipInput,
  success: VoiceTranscriptResult,
  error: Schema.Union([VoiceServiceError, EnvironmentAuthorizationError]),
});

export const WsVoiceSynthesizeSpeechRpc = Rpc.make(VOICE_WS_METHODS.synthesizeSpeech, {
  payload: VoiceSynthesizeSpeechInput,
  success: VoiceAudioChunk,
  error: Schema.Union([VoiceServiceError, EnvironmentAuthorizationError]),
  stream: true,
});

export const WsVoiceConversationStartRpc = Rpc.make(VOICE_WS_METHODS.conversationStart, {
  payload: VoiceConversationStartInput,
  success: VoiceConversationStartResult,
  error: Schema.Union([VoiceServiceError, EnvironmentAuthorizationError]),
});

export const WsVoiceConversationAppendAudioRpc = Rpc.make(
  VOICE_WS_METHODS.conversationAppendAudio,
  {
    payload: VoiceConversationAppendAudioInput,
    success: VoiceConversationAppendAudioResult,
    error: Schema.Union([VoiceServiceError, EnvironmentAuthorizationError]),
  },
);

export const WsVoiceConversationEndInputRpc = Rpc.make(VOICE_WS_METHODS.conversationEndInput, {
  payload: VoiceConversationEndInputInput,
  success: VoiceConversationEndInputResult,
  error: Schema.Union([VoiceServiceError, EnvironmentAuthorizationError]),
});

export const WsVoiceConversationStopRpc = Rpc.make(VOICE_WS_METHODS.conversationStop, {
  payload: VoiceConversationStopInput,
  success: VoiceConversationStopResult,
  error: Schema.Union([VoiceServiceError, EnvironmentAuthorizationError]),
});

export const WsVoiceConversationEventsRpc = Rpc.make(VOICE_WS_METHODS.conversationEvents, {
  payload: VoiceConversationEventsInput,
  success: VoiceConversationEvent,
  error: Schema.Union([VoiceServiceError, EnvironmentAuthorizationError]),
  stream: true,
});

export const WsTerminalOpenRpc = Rpc.make(WS_METHODS.terminalOpen, {
  payload: TerminalOpenInput,
  success: TerminalSessionSnapshot,
  error: Schema.Union([TerminalError, EnvironmentAuthorizationError]),
});

export const WsTerminalAttachRpc = Rpc.make(WS_METHODS.terminalAttach, {
  payload: TerminalAttachInput,
  success: TerminalAttachStreamEvent,
  error: Schema.Union([TerminalError, EnvironmentAuthorizationError]),
  stream: true,
});

export const WsTerminalWriteRpc = Rpc.make(WS_METHODS.terminalWrite, {
  payload: TerminalWriteInput,
  error: Schema.Union([TerminalError, EnvironmentAuthorizationError]),
});

export const WsTerminalResizeRpc = Rpc.make(WS_METHODS.terminalResize, {
  payload: TerminalResizeInput,
  error: Schema.Union([TerminalError, EnvironmentAuthorizationError]),
});

export const WsTerminalClearRpc = Rpc.make(WS_METHODS.terminalClear, {
  payload: TerminalClearInput,
  error: Schema.Union([TerminalError, EnvironmentAuthorizationError]),
});

export const WsTerminalRestartRpc = Rpc.make(WS_METHODS.terminalRestart, {
  payload: TerminalRestartInput,
  success: TerminalSessionSnapshot,
  error: Schema.Union([TerminalError, EnvironmentAuthorizationError]),
});

export const WsTerminalCloseRpc = Rpc.make(WS_METHODS.terminalClose, {
  payload: TerminalCloseInput,
  error: Schema.Union([TerminalError, EnvironmentAuthorizationError]),
});

export const WsPreviewOpenRpc = Rpc.make(WS_METHODS.previewOpen, {
  payload: PreviewOpenInput,
  success: PreviewSessionSnapshot,
  error: Schema.Union([PreviewError, EnvironmentAuthorizationError]),
});

export const WsPreviewNavigateRpc = Rpc.make(WS_METHODS.previewNavigate, {
  payload: PreviewNavigateInput,
  success: PreviewSessionSnapshot,
  error: Schema.Union([PreviewError, EnvironmentAuthorizationError]),
});

export const WsPreviewResizeRpc = Rpc.make(WS_METHODS.previewResize, {
  payload: PreviewResizeInput,
  success: PreviewSessionSnapshot,
  error: Schema.Union([PreviewError, EnvironmentAuthorizationError]),
});

export const WsPreviewRefreshRpc = Rpc.make(WS_METHODS.previewRefresh, {
  payload: PreviewRefreshInput,
  error: Schema.Union([PreviewError, EnvironmentAuthorizationError]),
});

export const WsPreviewCloseRpc = Rpc.make(WS_METHODS.previewClose, {
  payload: PreviewCloseInput,
  error: Schema.Union([PreviewError, EnvironmentAuthorizationError]),
});

export const WsPreviewListRpc = Rpc.make(WS_METHODS.previewList, {
  payload: PreviewListInput,
  success: PreviewListResult,
  error: EnvironmentAuthorizationError,
});

export const WsPreviewReportStatusRpc = Rpc.make(WS_METHODS.previewReportStatus, {
  payload: PreviewReportStatusInput,
  error: Schema.Union([PreviewError, EnvironmentAuthorizationError]),
});

export const WsPreviewAutomationConnectRpc = Rpc.make(WS_METHODS.previewAutomationConnect, {
  payload: PreviewAutomationHost,
  success: PreviewAutomationStreamEvent,
  error: Schema.Union([PreviewAutomationError, EnvironmentAuthorizationError]),
  stream: true,
});

export const WsPreviewAutomationRespondRpc = Rpc.make(WS_METHODS.previewAutomationRespond, {
  payload: PreviewAutomationResponse,
  error: Schema.Union([PreviewAutomationError, EnvironmentAuthorizationError]),
});

export const WsPreviewAutomationFocusHostRpc = Rpc.make(WS_METHODS.previewAutomationFocusHost, {
  payload: PreviewAutomationHostFocus,
  error: EnvironmentAuthorizationError,
});

export const WsSubscribePreviewEventsRpc = Rpc.make(WS_METHODS.subscribePreviewEvents, {
  payload: Schema.Struct({}),
  success: PreviewEvent,
  error: EnvironmentAuthorizationError,
  stream: true,
});

export const WsSubscribeDiscoveredLocalServersRpc = Rpc.make(
  WS_METHODS.subscribeDiscoveredLocalServers,
  {
    payload: Schema.Struct({}),
    success: DiscoveredLocalServerList,
    error: EnvironmentAuthorizationError,
    stream: true,
  },
);

export const WsOrchestrationDispatchCommandRpc = Rpc.make(
  ORCHESTRATION_WS_METHODS.dispatchCommand,
  {
    payload: ClientOrchestrationCommand,
    success: OrchestrationRpcSchemas.dispatchCommand.output,
    error: Schema.Union([OrchestrationDispatchCommandError, EnvironmentAuthorizationError]),
  },
);

export const WsOrchestrationGetTurnDiffRpc = Rpc.make(ORCHESTRATION_WS_METHODS.getTurnDiff, {
  payload: OrchestrationGetTurnDiffInput,
  success: OrchestrationRpcSchemas.getTurnDiff.output,
  error: Schema.Union([OrchestrationGetTurnDiffError, EnvironmentAuthorizationError]),
});

export const WsOrchestrationGetFullThreadDiffRpc = Rpc.make(
  ORCHESTRATION_WS_METHODS.getFullThreadDiff,
  {
    payload: OrchestrationGetFullThreadDiffInput,
    success: OrchestrationRpcSchemas.getFullThreadDiff.output,
    error: Schema.Union([OrchestrationGetFullThreadDiffError, EnvironmentAuthorizationError]),
  },
);

export const WsOrchestrationReplayEventsRpc = Rpc.make(ORCHESTRATION_WS_METHODS.replayEvents, {
  payload: OrchestrationReplayEventsInput,
  success: OrchestrationRpcSchemas.replayEvents.output,
  error: Schema.Union([OrchestrationReplayEventsError, EnvironmentAuthorizationError]),
});

export const WsOrchestrationGetArchivedShellSnapshotRpc = Rpc.make(
  ORCHESTRATION_WS_METHODS.getArchivedShellSnapshot,
  {
    payload: OrchestrationRpcSchemas.getArchivedShellSnapshot.input,
    success: OrchestrationRpcSchemas.getArchivedShellSnapshot.output,
    error: Schema.Union([OrchestrationGetSnapshotError, EnvironmentAuthorizationError]),
  },
);

export const WsOrchestrationSubscribeShellRpc = Rpc.make(ORCHESTRATION_WS_METHODS.subscribeShell, {
  payload: OrchestrationRpcSchemas.subscribeShell.input,
  success: OrchestrationRpcSchemas.subscribeShell.output,
  error: Schema.Union([OrchestrationGetSnapshotError, EnvironmentAuthorizationError]),
  stream: true,
});

export const WsOrchestrationSubscribeThreadRpc = Rpc.make(
  ORCHESTRATION_WS_METHODS.subscribeThread,
  {
    payload: OrchestrationRpcSchemas.subscribeThread.input,
    success: OrchestrationRpcSchemas.subscribeThread.output,
    error: Schema.Union([OrchestrationGetSnapshotError, EnvironmentAuthorizationError]),
    stream: true,
  },
);

export const WsSubscribeTerminalEventsRpc = Rpc.make(WS_METHODS.subscribeTerminalEvents, {
  payload: Schema.Struct({}),
  success: TerminalEvent,
  error: EnvironmentAuthorizationError,
  stream: true,
});

export const WsSubscribeTerminalMetadataRpc = Rpc.make(WS_METHODS.subscribeTerminalMetadata, {
  payload: Schema.Struct({}),
  success: TerminalMetadataStreamEvent,
  error: EnvironmentAuthorizationError,
  stream: true,
});

export const WsSubscribeServerConfigRpc = Rpc.make(WS_METHODS.subscribeServerConfig, {
  payload: Schema.Struct({}),
  success: ServerConfigStreamEvent,
  error: Schema.Union([KeybindingsConfigError, ServerSettingsError, EnvironmentAuthorizationError]),
  stream: true,
});

export const WsSubscribeServerLifecycleRpc = Rpc.make(WS_METHODS.subscribeServerLifecycle, {
  payload: Schema.Struct({}),
  success: ServerLifecycleStreamEvent,
  error: EnvironmentAuthorizationError,
  stream: true,
});

export const WsSubscribeAuthAccessRpc = Rpc.make(WS_METHODS.subscribeAuthAccess, {
  payload: Schema.Struct({}),
  success: AuthAccessStreamEvent,
  error: Schema.Union([AuthAccessStreamError, EnvironmentAuthorizationError]),
  stream: true,
});

export const WsRpcGroup = RpcGroup.make(
  WsServerProbeRpc,
  WsServerGetConfigRpc,
  WsServerRefreshProvidersRpc,
  WsServerUpdateProviderRpc,
  WsServerUpdateServerRpc,
  WsServerUpsertKeybindingRpc,
  WsServerRemoveKeybindingRpc,
  WsServerGetSettingsRpc,
  WsServerUpdateSettingsRpc,
  WsMcpGatewayGetStatusRpc,
  WsMcpGatewayTestConnectionRpc,
  WsMcpGatewaySetConnectionRpc,
  WsMcpGatewayListPluginsRpc,
  WsMcpGatewayActivatePluginRpc,
  WsMcpGatewayDeactivatePluginRpc,
  WsMcpGatewayStartOAuthRpc,
  WsWerkflowInstancesTestConnectionRpc,
  WsAgentWorkflowsGetStoreRpc,
  WsAgentWorkflowsCreateRpc,
  WsAgentWorkflowsUpdateScheduleRpc,
  WsAgentWorkflowsApplyChatCommandRpc,
  WsAgentWorkflowsRunOnceRpc,
  WsAgentWorkflowsResolveApprovalRpc,
  WsAgentWorkflowsCancelRunRpc,
  WsAgentWorkflowsValidateRpc,
  WsAgentWorkflowsApproveVersionRpc,
  WsAgentWorkflowsDeployVersionRpc,
  WsAgentWorkflowsGetDeploymentRpc,
  WsAgentWorkflowsRestartRunRpc,
  WsAgentWorkflowsReconcileRunRpc,
  WsConvexWorkflowSecretsSetRpc,
  WsConvexWorkflowSecretsStatusRpc,
  WsSubscribeAgentWorkflowRunsRpc,
  WsServerDiscoverSourceControlRpc,
  WsServerGetTraceDiagnosticsRpc,
  WsServerGetProcessDiagnosticsRpc,
  WsServerGetProcessResourceHistoryRpc,
  WsServerSignalProcessRpc,
  WsCloudGetRelayClientStatusRpc,
  WsCloudInstallRelayClientRpc,
  WsSourceControlLookupRepositoryRpc,
  WsSourceControlCloneRepositoryRpc,
  WsSourceControlPublishRepositoryRpc,
  WsProjectsListEntriesRpc,
  WsProjectsReadFileRpc,
  WsProjectsSearchEntriesRpc,
  WsProjectsWriteFileRpc,
  WsShellOpenInEditorRpc,
  WsFilesystemBrowseRpc,
  WsAssetsCreateUrlRpc,
  WsSubscribeVcsStatusRpc,
  WsVcsPullRpc,
  WsVcsRefreshStatusRpc,
  WsGitRunStackedActionRpc,
  WsGitResolvePullRequestRpc,
  WsGitPreparePullRequestThreadRpc,
  WsVcsListRefsRpc,
  WsVcsCreateWorktreeRpc,
  WsVcsRemoveWorktreeRpc,
  WsVcsCreateRefRpc,
  WsVcsSwitchRefRpc,
  WsVcsInitRpc,
  WsReviewGetDiffPreviewRpc,
  WsVoiceTranscribeClipRpc,
  WsVoiceSynthesizeSpeechRpc,
  WsVoiceConversationStartRpc,
  WsVoiceConversationAppendAudioRpc,
  WsVoiceConversationEndInputRpc,
  WsVoiceConversationStopRpc,
  WsVoiceConversationEventsRpc,
  WsTerminalOpenRpc,
  WsTerminalAttachRpc,
  WsTerminalWriteRpc,
  WsTerminalResizeRpc,
  WsTerminalClearRpc,
  WsTerminalRestartRpc,
  WsTerminalCloseRpc,
  WsSubscribeTerminalEventsRpc,
  WsSubscribeTerminalMetadataRpc,
  WsPreviewOpenRpc,
  WsPreviewNavigateRpc,
  WsPreviewResizeRpc,
  WsPreviewRefreshRpc,
  WsPreviewCloseRpc,
  WsPreviewListRpc,
  WsPreviewReportStatusRpc,
  WsPreviewAutomationConnectRpc,
  WsPreviewAutomationRespondRpc,
  WsPreviewAutomationFocusHostRpc,
  WsSubscribePreviewEventsRpc,
  WsSubscribeDiscoveredLocalServersRpc,
  WsSubscribeServerConfigRpc,
  WsSubscribeServerLifecycleRpc,
  WsSubscribeAuthAccessRpc,
  WsOrchestrationDispatchCommandRpc,
  WsOrchestrationGetTurnDiffRpc,
  WsOrchestrationGetFullThreadDiffRpc,
  WsOrchestrationReplayEventsRpc,
  WsOrchestrationGetArchivedShellSnapshotRpc,
  WsOrchestrationSubscribeShellRpc,
  WsOrchestrationSubscribeThreadRpc,
);
